import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Medal, Users, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const RANK = ["bg-[#FFD166] text-[#0A192F]", "bg-slate-200 text-[#0A192F]", "bg-[#F4A261] text-white", "bg-white/10 text-white", "bg-white/10 text-white"];

const Row = ({ r, i }) => (
  <li className="flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 px-4 py-3 rs-poster-in" style={{ animationDelay: `${i * 90}ms` }} data-testid={`ambassador-row-${r.rank}`}>
    <span className={`w-10 h-10 rounded-full grid place-items-center font-black font-['Outfit'] shrink-0 ${RANK[i]}`}>{i === 0 ? <Crown className="w-5 h-5" /> : r.rank}</span>
    <div className="min-w-0 flex-1">
      <div className="font-bold text-white truncate" data-testid={`ambassador-name-${r.rank}`}>{r.name}{r.city && <span className="font-normal text-slate-400"> · {r.city}</span>}</div>
      {r.badge && <span className="text-[11px] font-bold text-[#FFD166]">{r.badge}</span>}
    </div>
    <span className="text-right"><span className="block text-2xl font-black text-white font-['Outfit'] tabular-nums" data-testid={`ambassador-friends-${r.rank}`}>{r.friends}</span><span className="text-[10px] uppercase tracking-widest text-slate-400">friends</span></span>
  </li>
);

export const TopAmbassadors = () => {
  const { L } = useLang();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/referrals/leaderboard").then((r) => setD(r.data)).catch(() => setD({ items: [] })); }, []);
  if (!d) return null;
  const month = d.month ? new Date(`${d.month}-01T00:00:00`).toLocaleDateString(L("en-IN", "hi-IN"), { month: "long", year: "numeric" }) : "";
  return (
    <section className="bg-[#0A192F] py-20 relative overflow-hidden" data-testid="top-ambassadors">
      <div className="absolute -top-24 right-0 w-96 h-96 rounded-full bg-[#FF3B3B]/20 blur-3xl" />
      <div className="relative max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[1fr_1.2fr] gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#FFD166]"><Medal className="w-4 h-4" />{L("Top Ambassadors", "टॉप एंबेसडर")} · {month}</span>
          <h2 className="text-3xl md:text-4xl font-black text-white font-['Outfit'] mt-3 leading-tight">{L("Families who gave wings to their friends' dreams", "जिन परिवारों ने दोस्तों के सपनों को पंख दिए")}</h2>
          <p className="text-slate-300 mt-3 max-w-md">{L("Share your coupon win with friends. Every friend who joins earns your family a thank-you and a new badge.", "अपनी कूपन जीत दोस्तों के साथ शेयर करें। हर जुड़ने वाले दोस्त पर आपके परिवार को धन्यवाद और नया बैज मिलता है।")}</p>
          <Link to="/enroll" className="inline-flex items-center gap-1 mt-6 rs-cta rounded-full px-5 py-2.5 text-sm font-bold" data-testid="ambassadors-cta">{L("Get your coupon & share", "कूपन लें और शेयर करें")} <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {d.items.length ? (
          <ol className="space-y-3" data-testid="ambassadors-list">{d.items.map((r, i) => <Row key={r.rank} r={r} i={i} />)}</ol>
        ) : (
          <div className="rounded-2xl border border-dashed border-white/20 p-10 text-center text-slate-300" data-testid="ambassadors-empty">
            <Users className="w-10 h-10 mx-auto text-[#FFD166]" />
            <p className="mt-3 font-bold text-white">{L("Be this month's first ambassador!", "इस महीने के पहले एंबेसडर बनें!")}</p>
            <p className="text-sm mt-1">{L("Families appear here only if they choose to.", "परिवार केवल अपनी मर्ज़ी से यहाँ दिखते हैं।")}</p>
          </div>
        )}
      </div>
    </section>
  );
};
